import React from "react";
import { Button, Form, Input } from "antd";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import httpClient from "../axios";
import { notifyError, notifySuccess } from "../helper";
import { saveLogin } from "../lib/auth";
import { Split, Hero, FormPane, FormBox } from "./Login";
import { ReactComponent as VrtsLogo } from "../Assets/svg/VRTS_Logo.svg";

/** Landing page for the link in the password-reset email (?token=…&email=…). */
const ResetPassword = () => {
	const navigate = useNavigate();
	const [params] = useSearchParams();
	const http = React.useMemo(() => httpClient(), []);
	const [loading, setLoading] = React.useState(false);

	const onFinish = (values) => {
		setLoading(true);
		http
			.post("/auth/reset-password", {
				token: values.token,
				email: values.email.trim(),
				password: values.password,
				password_confirmation: values.password_confirmation,
			})
			.then((response) => {
				saveLogin(response.data);
				notifySuccess("Password updated", "You are now signed in.");
				navigate("/mode-selection");
			})
			.catch((error) => notifyError(error, "This reset link is invalid or has expired"))
			.finally(() => setLoading(false));
	};

	return (
		<Split>
			<Hero>
				<div>
					<VrtsLogo />
					<h1>Choose a new password</h1>
					<p>The link from your email is valid for a limited time.</p>
				</div>
			</Hero>
			<FormPane>
				<FormBox>
					<h2>Reset password</h2>
					<div className="hint">Enter and confirm your new password.</div>
					<Form
						layout="vertical"
						onFinish={onFinish}
						requiredMark={false}
						size="large"
						initialValues={{ token: params.get("token") || "", email: params.get("email") || "" }}
					>
						<Form.Item name="token" label="Reset token" rules={[{ required: true, message: "Missing reset token" }]}>
							<Input placeholder="Token from the email link" />
						</Form.Item>
						<Form.Item
							name="email"
							label="Email"
							rules={[{ required: true, type: "email", message: "Enter your email address" }]}
						>
							<Input placeholder="you@example.com" />
						</Form.Item>
						<Form.Item
							name="password"
							label="New password"
							rules={[{ required: true, min: 8, message: "At least 8 characters" }]}
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
						<Form.Item
							name="password_confirmation"
							label="Confirm new password"
							dependencies={["password"]}
							rules={[
								{ required: true, message: "Confirm the new password" },
								({ getFieldValue }) => ({
									validator(_, value) {
										return !value || getFieldValue("password") === value
											? Promise.resolve()
											: Promise.reject(new Error("Passwords do not match"));
									},
								}),
							]}
						>
							<Input.Password autoComplete="new-password" />
						</Form.Item>
						<Button type="primary" htmlType="submit" block loading={loading}>
							Save new password
						</Button>
					</Form>
					<div style={{ marginTop: 16, textAlign: "center" }}>
						<Link to="/">Back to sign in</Link>
					</div>
				</FormBox>
			</FormPane>
		</Split>
	);
};

export default ResetPassword;
